import React, { useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  APIClient,
  getLoggedinSuperAdmin,
  persistSuperAdminSession,
  saUrl,
} from "../../../helpers/api_helper";

const api = new APIClient();

/**
 * Conta SA — trocar e-mail/senha sem depender de seed DEV (DOC 02).
 * Emergência sem sessão: artisan brenvio:ops:set-super-admin.
 */
const SuperAdminAccount = () => {
  const { t } = useTranslation();
  const session = getLoggedinSuperAdmin();
  const [loader, setLoader] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: session?.user?.email || "",
      current_password: "",
      password: "",
      password_confirmation: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email(t("sa.account.validation.emailInvalid"))
        .required(t("sa.account.validation.emailRequired")),
      current_password: Yup.string().required(
        t("sa.account.validation.currentRequired")
      ),
      // Em branco = não troca senha; se preenchida, mínimo 12.
      password: Yup.string()
        .transform((v) => (v === "" ? undefined : v))
        .min(12, t("sa.account.validation.passwordMin")),
      password_confirmation: Yup.string().when("password", {
        is: (v: string | undefined) => !!v,
        then: (schema) =>
          schema
            .required(t("sa.account.validation.passwordMismatch"))
            .oneOf(
              [Yup.ref("password")],
              t("sa.account.validation.passwordMismatch")
            ),
      }),
    }),
    onSubmit: async (values) => {
      setLoader(true);
      setErrorMessage(null);
      setSuccessMessage(null);
      try {
        const payload: Record<string, string> = {
          current_password: values.current_password,
        };
        if (values.email && values.email !== session?.user?.email) {
          payload.email = values.email;
        }
        if (values.password) {
          payload.password = values.password;
          payload.password_confirmation = values.password_confirmation;
        }
        if (!payload.email && !payload.password) {
          setErrorMessage(t("sa.account.error.nothingToChange"));
          setLoader(false);
          return;
        }

        const envelope: any = await api.update(
          saUrl("/super-admin/me/credentials"),
          payload
        );
        if (!envelope?.success) {
          setErrorMessage(
            envelope?.error?.message || t("sa.account.error.generic")
          );
          setLoader(false);
          return;
        }

        // Mantém JWT; só atualiza o user na sessão local.
        if (session) {
          persistSuperAdminSession({
            ...session,
            user: envelope.data.user,
          });
        }
        setSuccessMessage(t("sa.account.success"));
        validation.setFieldValue("current_password", "");
        validation.setFieldValue("password", "");
        validation.setFieldValue("password_confirmation", "");
        setLoader(false);
      } catch (err: any) {
        setErrorMessage(err?.message || t("sa.account.error.generic"));
        setLoader(false);
      }
    },
  });

  document.title = t("sa.account.documentTitle");

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("sa.account.title")}
          pageTitle={t("sa.header.role")}
        />
        <Row className="justify-content-center">
          <Col lg={6}>
            <Card>
              <CardBody>
                <p className="text-muted">{t("sa.account.subtitle")}</p>

                {errorMessage ? (
                  <Alert color="danger" role="alert">
                    {errorMessage}
                  </Alert>
                ) : null}
                {successMessage ? (
                  <Alert color="success" role="status">
                    {successMessage}
                  </Alert>
                ) : null}

                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    validation.handleSubmit();
                    return false;
                  }}
                >
                  <div className="mb-3">
                    <Label htmlFor="sa-account-email">{t("sa.account.email")}</Label>
                    <Input
                      id="sa-account-email"
                      name="email"
                      type="email"
                      value={validation.values.email}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      invalid={
                        !!(
                          validation.touched.email && validation.errors.email
                        )
                      }
                    />
                    {validation.touched.email && validation.errors.email ? (
                      <FormFeedback type="invalid">
                        {String(validation.errors.email)}
                      </FormFeedback>
                    ) : null}
                  </div>

                  <div className="mb-3">
                    <Label htmlFor="sa-account-current">
                      {t("sa.account.currentPassword")}
                    </Label>
                    <Input
                      id="sa-account-current"
                      name="current_password"
                      type="password"
                      autoComplete="current-password"
                      value={validation.values.current_password}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      invalid={
                        !!(
                          validation.touched.current_password &&
                          validation.errors.current_password
                        )
                      }
                    />
                    {validation.touched.current_password &&
                    validation.errors.current_password ? (
                      <FormFeedback type="invalid">
                        {String(validation.errors.current_password)}
                      </FormFeedback>
                    ) : null}
                  </div>

                  <div className="mb-3">
                    <Label htmlFor="sa-account-password">
                      {t("sa.account.newPassword")}
                    </Label>
                    <Input
                      id="sa-account-password"
                      name="password"
                      type="password"
                      autoComplete="new-password"
                      value={validation.values.password}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      invalid={
                        !!(
                          validation.touched.password &&
                          validation.errors.password
                        )
                      }
                    />
                    {validation.touched.password &&
                    validation.errors.password ? (
                      <FormFeedback type="invalid">
                        {String(validation.errors.password)}
                      </FormFeedback>
                    ) : null}
                    <div className="form-text">{t("sa.account.passwordHint")}</div>
                  </div>

                  <div className="mb-3">
                    <Label htmlFor="sa-account-password-confirm">
                      {t("sa.account.confirmPassword")}
                    </Label>
                    <Input
                      id="sa-account-password-confirm"
                      name="password_confirmation"
                      type="password"
                      autoComplete="new-password"
                      value={validation.values.password_confirmation}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      invalid={
                        !!(
                          validation.touched.password_confirmation &&
                          validation.errors.password_confirmation
                        )
                      }
                    />
                    {validation.touched.password_confirmation &&
                    validation.errors.password_confirmation ? (
                      <FormFeedback type="invalid">
                        {String(validation.errors.password_confirmation)}
                      </FormFeedback>
                    ) : null}
                  </div>

                  <Button color="primary" type="submit" disabled={loader}>
                    {loader ? (
                      <Spinner size="sm" className="me-2">
                        …
                      </Spinner>
                    ) : null}
                    {t("sa.account.submit")}
                  </Button>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default SuperAdminAccount;
